// Simple typeahead combobox — searchable Select replacement.
// Usage: <Typeahead options={[{value,label,sub?}]} value={v} onChange={fn} onCreate={fn?} placeholder="…" />
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Plus, Search, X } from "lucide-react";

export function Typeahead({ options = [], value, onChange, onCreate, placeholder = "Type to search…", createLabel = "Add new", testid, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const filtered = q ? options.filter((o) => (o.label + " " + (o.sub || "")).toLowerCase().includes(q.toLowerCase())) : options;

  const pick = (o) => { onChange(o.value); setOpen(false); setQ(""); };
  const clear = (e) => { e.stopPropagation(); onChange(""); setQ(""); };

  return (
    <div className="relative" ref={boxRef}>
      <button type="button" data-testid={testid}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-[#E8E2D9] bg-white text-sm text-left ${disabled ? "opacity-60" : "hover:border-[#E8712C]/40"}`}>
        <span className={selected ? "text-[#1C1917] truncate" : "text-[#A8A29E]"}>
          {selected?.label || placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selected && !disabled && <span onClick={clear} className="p-0.5 hover:bg-[#F5F5F4] rounded"><X size={13} className="text-[#A8A29E]" /></span>}
          <ChevronDown size={14} className="text-[#A8A29E]" />
        </div>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E8E2D9] rounded-lg shadow-xl z-30 overflow-hidden">
          <div className="relative border-b border-[#E8E2D9]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              className="w-full pl-9 pr-3 py-2.5 text-sm focus:outline-none" />
          </div>
          <ul className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && !onCreate && (
              <li className="px-3 py-3 text-sm text-[#A8A29E] text-center">No matches</li>
            )}
            {filtered.slice(0, 30).map((o) => (
              <li key={o.value}>
                <button type="button" onClick={() => pick(o)}
                  data-testid={`${testid}-opt-${o.value}`}
                  className="w-full text-left px-3 py-2 hover:bg-[#FAF8F5] flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{o.label}</div>
                    {o.sub && <div className="text-xs text-[#A8A29E] truncate">{o.sub}</div>}
                  </div>
                  {o.value === value && <span className="text-xs text-[#E8712C]">✓</span>}
                </button>
              </li>
            ))}
            {onCreate && q && !filtered.some((o) => o.label.toLowerCase() === q.toLowerCase()) && (
              <li className="border-t border-[#E8E2D9]">
                <button type="button" data-testid={`${testid}-create`}
                  onClick={() => { onCreate(q); setOpen(false); setQ(""); }}
                  className="w-full text-left px-3 py-2.5 hover:bg-[#FDE9D8]/40 flex items-center gap-2 text-[#E8712C]">
                  <Plus size={14} /> <span className="text-sm">{createLabel} "{q}"</span>
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export const STAFF_ROLES = [
  "Care Coordinator",
  "Intake Coordinator",
  "Virtual Assistant",
  "Case Manager",
  "Administrative Assistant",
  "Provider",
  "Owner",
  "Other",
];
